import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Crown, Lock } from 'lucide-react';

export const ProtectedRoute = ({ children, requiredPlan }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) return;
    
    if (!loading) {
      setIsAuthenticated(!!user);
    }
  }, [user, loading, location.state]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-bold">Cargando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check plan access
  if (requiredPlan) {
    const userPlan = user?.subscription_plan || 'basic';
    
    const hasAccess = () => {
      if (requiredPlan === 'premium') {
        return userPlan === 'premium' || userPlan === 'profesional';
      }
      if (requiredPlan === 'essential') {
        return ['essential', 'premium', 'profesional', 'usuario_avanzado'].includes(userPlan);
      }
      return true;
    };
    
    if (!hasAccess()) {
      const planName = requiredPlan === 'premium' ? 'Profesional' : 'Avanzado';
      const planPrice = requiredPlan === 'premium' ? '€24.99' : '€4.99';
      
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            {/* Blurred preview */}
            <div className="relative">
              <div 
                className="bg-gray-900 rounded-2xl border border-white/10 p-12"
                style={{ filter: 'blur(10px)', pointerEvents: 'none', userSelect: 'none' }}
              >
                <div className="space-y-4">
                  <div className="h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-700 rounded w-full"></div>
                  <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-700 rounded w-4/6"></div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    <div className="h-32 bg-gray-800 rounded"></div>
                    <div className="h-32 bg-gray-800 rounded"></div>
                    <div className="h-32 bg-gray-800 rounded"></div>
                    <div className="h-32 bg-gray-800 rounded"></div>
                  </div>
                </div>
              </div>
              
              {/* Upgrade overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-gray-900/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 border-4 border-yellow-500/50 max-w-lg text-center shadow-2xl">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 mb-4">
                      <Crown size={40} className="text-black" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">
                      Acceso Exclusivo
                    </h2>
                    <p className="text-gray-400 text-lg">
                      Esta funcionalidad está disponible solo para suscriptores {planName}
                    </p>
                  </div>
                  
                  <div className="bg-white/5 rounded-xl p-6 mb-6 border border-white/10">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <Lock size={20} className="text-yellow-400" />
                      <span className="text-white font-semibold">Plan {planName}</span>
                    </div>
                    <div className="text-4xl font-bold text-white mb-2">
                      {planPrice}<span className="text-xl text-gray-400">/mes</span>
                    </div>
                    {requiredPlan === 'premium' && (
                      <p className="text-sm text-gray-400">
                        Incluye CRM, Organizaciones, Mis Tareas y más
                      </p>
                    )}
                    {requiredPlan === 'essential' && (
                      <p className="text-sm text-gray-400">
                        Incluye Invertir, Conocimiento, Comunidad, AI datos claros
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <Link
                      to="/select-plan"
                      className="block w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold py-4 px-6 rounded-xl hover:scale-105 transition-all duration-300 shadow-xl"
                    >
                      Ver Planes y Actualizar
                    </Link>
                    <Link
                      to="/dashboard"
                      className="block w-full bg-white/5 text-white font-medium py-3 px-6 rounded-xl hover:bg-white/10 transition-all duration-300 border border-white/10"
                    >
                      Volver al Mapa
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

